import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Select, Row, Col, Statistic, Tag, Typography, Spin,
  DatePicker, Button, Space, Modal, Tree, Tooltip, Input, Divider, message,
} from 'antd';
import {
  TeamOutlined, UserOutlined, GlobalOutlined, MobileOutlined, PhoneOutlined,
  CalendarFilled, FileExcelOutlined, PrinterOutlined, ApartmentOutlined,
  SearchOutlined, ReloadOutlined, FilterOutlined,
} from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import * as XLSX from 'xlsx';
import axios from 'axios';
import moment from 'moment';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

const SOURCE_LABELS = {
  member:     'सदस्य के माध्यम से',
  walk_in:    'सीधे कार्यालय आकर',
  website:    'वेबसाइट द्वारा',
  app:        'एप्लिकेशन द्वारा',
  phone_call: 'फोन कॉल द्वारा',
  event:      'कार्यक्रम में',
  other:      'अन्य',
};
const SOURCE_COLORS = {
  member: 'blue', walk_in: 'green', website: 'purple',
  app: 'orange', phone_call: 'cyan', event: 'magenta', other: 'default',
};
const SOURCE_CHART_COLORS = ['#1677ff','#52c41a','#722ed1','#fa8c16','#13c2c2','#eb2f96','#8c8c8c'];

export default function ReferralReport() {
  const [filters, setFilters] = useState({ dateRange: null, source: '', referrer_id: '', search: '' });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  // Tree modal
  const [treeVisible, setTreeVisible]   = useState(false);
  const [treeData, setTreeData]         = useState(null);
  const [treeLoading, setTreeLoading]   = useState(false);
  const [treeRoot, setTreeRoot]         = useState(null);

  const fetchReport = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.dateRange?.[0]) { params.start_date = f.dateRange[0].format('YYYY-MM-DD'); }
      if (f.dateRange?.[1]) { params.end_date   = f.dateRange[1].format('YYYY-MM-DD'); }
      if (f.source)        params.source       = f.source;
      if (f.referrer_id)   params.referrer_id  = f.referrer_id;
      const res = await axios.get(`${baseUrl}/users/referral-report`, { ...AUTH(), params });
      setData(res.data.result);
    } catch (e) {
      message.error('डेटा लोड नहीं हुआ');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReport(); }, []); // eslint-disable-line

  const handleReset = () => {
    const f = { dateRange: null, source: '', referrer_id: '', search: '' };
    setFilters(f);
    fetchReport(f);
  };

  // ── Referral tree ──────────────────────────────────────────────────────────
  const openTree = async (member) => {
    setTreeRoot(member);
    setTreeVisible(true);
    setTreeLoading(true);
    setTreeData(null);
    try {
      const res = await axios.get(`${baseUrl}/users/referral-tree/${member.referrer_id}`, AUTH());
      if (res.data.status) {
        // Build antd Tree data
        const buildTree = (parentId, allNodes) => {
          return allNodes
            .filter(n => n.parent_id === parentId)
            .map(n => ({
              title: (
                <span>
                  <Text strong>{n.name}</Text>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{n.phone_no}</Text>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 6 }}>
                    {n.joining_date ? moment(n.joining_date).format('DD MMM YYYY') : ''}
                  </Text>
                </span>
              ),
              key: String(n.id),
              children: buildTree(n.id, allNodes),
            }));
        };
        const root = res.data.root;
        setTreeData([{
          title: (
            <span style={{ fontWeight: 700, color: '#1677ff' }}>
              {root.name}
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6, fontWeight: 400 }}>{root.phone_no}</Text>
            </span>
          ),
          key: String(root.id),
          children: buildTree(root.id, res.data.nodes),
        }]);
      }
    } catch (_) { message.error('Tree load failed'); }
    finally { setTreeLoading(false); }
  };

  // ── Export Excel ───────────────────────────────────────────────────────────
  const handleExport = () => {
    const members = filteredMembers;
    if (!members.length) { message.warning('कोई डेटा नहीं'); return; }
    const rows = members.map((m, i) => ({
      'S.No':             i + 1,
      'सदस्य नाम':       m.name || '',
      'पिता/पति नाम':    m.father_name || '',
      'मोबाइल':          m.phone_no || '',
      'गाँव':            m.village || '',
      'तहसील':           m.tehsil || '',
      'जिला':            m.district || '',
      'जुड़ने की दिनांक': m.joining_date ? moment(m.joining_date).format('DD-MM-YYYY') : '',
      'माध्यम':          SOURCE_LABELS[m.ref_source] || m.ref_source || '',
      'संदर्भ सदस्य':    m.ref_member_name || '',
      'संदर्भ मोबाइल':   m.ref_member_phone || '',
      'नोट':             m.ref_note || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Referral Report');
    XLSX.writeFile(wb, `referral_report_${moment().format('DDMMYYYY')}.xlsx`);
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Source summary ─────────────────────────────────────────────────────────
  const summaryBySource = {};
  (data?.summary || []).forEach(r => { summaryBySource[r.ref_source] = r.total; });

  // ── Monthly trend chart ────────────────────────────────────────────────────
  const monthlyChartData = (() => {
    const trend = data?.monthlyTrend || [];
    const months = [...new Set(trend.map(r => r.month))].sort();
    const sources = [...new Set(trend.map(r => r.ref_source))];
    const maxVal = Math.max(1, ...trend.map(r => r.cnt));
    return {
      options: {
        chart: { type: 'bar', toolbar: { show: false }, stacked: true, fontFamily: 'inherit' },
        xaxis: {
          categories: months.map(m => moment(m, 'YYYY-MM').format('MMM YYYY')),
          labels: { style: { fontSize: '12px', fontWeight: 600 } },
          title: { text: 'महीना', style: { fontSize: '12px', color: '#555' } },
        },
        yaxis: {
          tickAmount: Math.min(maxVal, 10),
          min: 0,
          max: maxVal + 1,
          labels: {
            formatter: v => Math.round(v),
            style: { fontSize: '12px' },
          },
          title: { text: 'सदस्य संख्या', style: { fontSize: '12px', color: '#555' } },
        },
        colors: SOURCE_CHART_COLORS,
        legend: { position: 'top', fontSize: '12px' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        dataLabels: {
          enabled: true,
          formatter: v => v > 0 ? v : '',
          style: { fontSize: '12px', fontWeight: 700, colors: ['#fff'] },
        },
        tooltip: {
          y: { formatter: v => `${v} सदस्य` },
          theme: 'light',
        },
        grid: { borderColor: '#f0f0f0' },
      },
      series: sources.map(src => ({
        name: SOURCE_LABELS[src] || src,
        data: months.map(m => {
          const found = trend.find(r => r.month === m && r.ref_source === src);
          return found ? Number(found.cnt) : 0;
        }),
      })),
    };
  })();

  // ── Source donut chart ─────────────────────────────────────────────────────
  const summaryList = data?.summary || [];
  const sourcePieData = {
    options: {
      chart: { type: 'donut', fontFamily: 'inherit' },
      labels: summaryList.map(r => SOURCE_LABELS[r.ref_source] || r.ref_source),
      colors: SOURCE_CHART_COLORS,
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const count = opts.w.config.series[opts.seriesIndex];
          return `${count} (${Math.round(val)}%)`;
        },
        style: { fontSize: '13px', fontWeight: 700 },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'कुल',
                fontSize: '14px',
                fontWeight: 700,
                color: '#7a0000',
                formatter: w => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      tooltip: { y: { formatter: v => `${v} सदस्य` } },
    },
    series: summaryList.map(r => Number(r.total)),
  };

  // ── Leaderboard bar chart (top 10) ─────────────────────────────────────────
  const leaderboard = data?.leaderboard || [];
  const top10 = leaderboard.slice(0, 10);
  const maxCount = Math.max(1, ...top10.map(r => r.referred_count));
  const leaderChartData = {
    options: {
      chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '60%',
          dataLabels: { position: 'right' },
        },
      },
      colors: ['#1677ff'],
      xaxis: {
        categories: top10.map(r => r.referrer_name),
        min: 0,
        max: maxCount + 1,
        tickAmount: Math.min(maxCount, 10),
        labels: {
          formatter: v => Math.round(v),
          style: { fontSize: '12px' },
        },
        title: { text: 'लाए गए सदस्य', style: { fontSize: '12px', color: '#555' } },
      },
      yaxis: {
        labels: { style: { fontSize: '12px', fontWeight: 600 } },
      },
      dataLabels: {
        enabled: true,
        formatter: v => `${v} सदस्य`,
        offsetX: 8,
        style: { fontSize: '12px', fontWeight: 700, colors: ['#7a0000'] },
      },
      tooltip: { x: { show: true }, y: { formatter: v => `${v} सदस्य लाए` } },
      grid: { borderColor: '#f0f0f0', xaxis: { lines: { show: true } } },
    },
    series: [{ name: 'लाए गए सदस्य', data: top10.map(r => Number(r.referred_count)) }],
  };

  // ── Filtered members ───────────────────────────────────────────────────────
  const filteredMembers = (data?.members || []).filter(m => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return (m.name || '').toLowerCase().includes(s) ||
           (m.phone_no || '').includes(s) ||
           (m.ref_member_name || '').toLowerCase().includes(s) ||
           (m.village || '').toLowerCase().includes(s) ||
           (m.district || '').toLowerCase().includes(s);
  });

  // ── Columns ────────────────────────────────────────────────────────────────
  const leaderboardCols = [
    { title: '#', width: 45, render: (_, __, i) => <Text type="secondary">{i + 1}</Text> },
    {
      title: 'संदर्भकर्ता का नाम', dataIndex: 'referrer_name', key: 'name',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          <div style={{ fontSize: 11, color: '#888' }}>{r.referrer_phone}</div>
        </div>
      )
    },
    {
      title: 'लाए गए सदस्य', dataIndex: 'referred_count', key: 'count', align: 'center',
      render: v => <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px', fontWeight: 700 }}>{v}</Tag>,
      sorter: (a, b) => b.referred_count - a.referred_count,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'नेटवर्क', key: 'tree', width: 90, align: 'center',
      render: (_, r) => (
        <Tooltip title="रेफरल चेन देखें">
          <Button size="small" icon={<ApartmentOutlined />} onClick={() => openTree(r)}
            style={{ color: '#722ed1', borderColor: '#722ed1' }}>Tree</Button>
        </Tooltip>
      ),
    },
  ];

  const memberCols = [
    { title: '#', width: 45, render: (_, __, i) => <Text type="secondary">{i + 1}</Text> },
    {
      title: 'सदस्य नाम', dataIndex: 'name', key: 'name', width: 160,
      render: (v, r) => (
        <span>
          <Text strong>{v}</Text>
          <br /><Text type="secondary" style={{ fontSize: 11 }}>{r.father_name}</Text>
        </span>
      )
    },
    { title: 'मोबाइल', dataIndex: 'phone_no', key: 'phone', width: 115 },
    {
      title: 'गाँव / तहसील / जिला', key: 'location', width: 190,
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.village && <div><Text strong>{r.village}</Text></div>}
          {(r.tehsil || r.district) && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {[r.tehsil, r.district].filter(Boolean).join(' · ')}
            </Text>
          )}
          {!r.village && !r.tehsil && !r.district && '—'}
        </span>
      ),
    },
    {
      title: 'जुड़ने की दिनांक', dataIndex: 'joining_date', key: 'jd', width: 125,
      render: v => v ? moment(v).format('DD MMM YYYY') : '—',
      sorter: (a, b) => new Date(a.joining_date) - new Date(b.joining_date),
    },
    {
      title: 'माध्यम', dataIndex: 'ref_source', key: 'src', width: 155,
      render: v => v ? <Tag color={SOURCE_COLORS[v] || 'default'}>{SOURCE_LABELS[v] || v}</Tag> : '—',
      filters: Object.keys(SOURCE_LABELS).map(k => ({ text: SOURCE_LABELS[k], value: k })),
      onFilter: (val, r) => r.ref_source === val,
    },
    {
      title: 'संदर्भ सदस्य', dataIndex: 'ref_member_name', key: 'rm', width: 155,
      render: (v, r) => v
        ? <span><Text strong>{v}</Text><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.ref_member_phone}</Text></span>
        : '—',
    },
    { title: 'नोट', dataIndex: 'ref_note', key: 'note', render: v => v || '—' },
  ];

  const hasData  = data?.monthlyTrend?.length > 0;
  const hasPie   = (data?.summary || []).length > 0;
  const hasLeader = top10.length > 0;

  return (
    <div style={{ padding: '16px 20px', background: '#fff', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>रेफरल / संदर्भ रिपोर्ट</Title>
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExport} style={{ borderColor: '#52c41a', color: '#52c41a' }}>Excel</Button>
          <Button icon={<PrinterOutlined />}   onClick={handlePrint}>Print</Button>
        </Space>
      </div>

      {/* ── Filters ── */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8, background: '#f8f9fa' }} bodyStyle={{ padding: '12px 16px' }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={8} md={7}>
            <RangePicker
              format="DD-MM-YYYY"
              placeholder={['शुरू दिनांक', 'अंत दिनांक']}
              value={filters.dateRange}
              onChange={v => setFilters(p => ({ ...p, dateRange: v }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="माध्यम"
              value={filters.source || undefined}
              onChange={v => setFilters(p => ({ ...p, source: v || '' }))}
              style={{ width: '100%' }}
              allowClear
            >
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <Input
              placeholder="नाम / मोबाइल खोजें"
              value={filters.search}
              onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<FilterOutlined />}
                onClick={() => fetchReport(filters)}
                style={{ background: '#7a0000', borderColor: '#7a0000' }}>खोजें</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* ── Summary Cards ── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6} md={4}>
              <Card size="small" style={{ textAlign: 'center', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 10 }}>
                <Statistic title="कुल रेफरल दर्ज" value={data?.total_with_ref || 0} valueStyle={{ color: '#1677ff', fontSize: 26, fontWeight: 800 }} />
              </Card>
            </Col>
            {Object.entries(SOURCE_LABELS).map(([key, label]) =>
              summaryBySource[key] ? (
                <Col xs={12} sm={6} md={4} key={key}>
                  <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                    <Statistic
                      title={<span style={{ fontSize: 11 }}>{label}</span>}
                      value={summaryBySource[key]}
                      valueStyle={{ fontSize: 22, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              ) : null
            )}
          </Row>

          {/* ── Charts Row ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {hasPie && (
              <Col xs={24} md={8}>
                <Card size="small" title="माध्यम अनुसार वितरण" style={{ borderRadius: 10 }}
                  headStyle={{ fontWeight: 700, fontSize: 13 }}>
                  <ReactApexChart
                    type="donut"
                    options={sourcePieData.options}
                    series={sourcePieData.series}
                    height={260}
                  />
                </Card>
              </Col>
            )}
            {hasLeader && (
              <Col xs={24} md={hasPie ? 16 : 24}>
                <Card size="small" title="शीर्ष संदर्भकर्ता (Top 10)" style={{ borderRadius: 10 }}
                  headStyle={{ fontWeight: 700, fontSize: 13 }}>
                  <ReactApexChart
                    type="bar"
                    options={leaderChartData.options}
                    series={leaderChartData.series}
                    height={Math.max(180, top10.length * 32)}
                  />
                </Card>
              </Col>
            )}
          </Row>

          {hasData && (
            <Card size="small" title="मासिक रेफरल ट्रेंड" style={{ marginBottom: 16, borderRadius: 10 }}
              headStyle={{ fontWeight: 700, fontSize: 13 }}>
              <ReactApexChart
                type="bar"
                options={monthlyChartData.options}
                series={monthlyChartData.series}
                height={220}
              />
            </Card>
          )}

          {/* ── Leaderboard Table ── */}
          <Card
            size="small"
            title={<span><TeamOutlined style={{ marginRight: 6, color: '#1677ff' }} />सक्रिय संदर्भकर्ता — Leaderboard</span>}
            style={{ marginBottom: 16, borderRadius: 10 }}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Tree बटन से संदर्भ श्रृंखला देखें</Text>}
          >
            <Table
              dataSource={leaderboard}
              columns={leaderboardCols}
              rowKey="referrer_id"
              size="small"
              pagination={{ pageSize: 10, size: 'small' }}
              locale={{ emptyText: 'कोई सदस्य-रेफरल दर्ज नहीं' }}
            />
          </Card>

          {/* ── All Referred Members ── */}
          <Card
            size="small"
            title={`सभी संदर्भित सदस्य (${filteredMembers.length})`}
            style={{ borderRadius: 10 }}
          >
            <Table
              dataSource={filteredMembers}
              columns={memberCols}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, size: 'small', showTotal: t => `कुल ${t} सदस्य` }}
              scroll={{ x: 900 }}
              locale={{ emptyText: 'कोई रेफरल डेटा नहीं मिला' }}
            />
          </Card>
        </>
      )}

      {/* ── Referral Tree Modal ── */}
      <Modal
        title={
          <Space>
            <ApartmentOutlined style={{ color: '#722ed1' }} />
            <span>रेफरल नेटवर्क — {treeRoot?.referrer_name}</span>
            <Tag color="purple">{treeRoot?.referrer_phone}</Tag>
          </Space>
        }
        open={treeVisible}
        onCancel={() => { setTreeVisible(false); setTreeData(null); }}
        footer={null}
        width={680}
      >
        {treeLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : treeData ? (
          <>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              नीचे दिखाए गए सदस्य इस संदर्भकर्ता के माध्यम से जुड़े हैं (4 स्तर तक)
            </Text>
            <Tree
              treeData={treeData}
              defaultExpandAll
              showLine={{ showLeafIcon: false }}
              style={{ fontSize: 13 }}
            />
          </>
        ) : (
          <Text type="secondary">कोई संदर्भित सदस्य नहीं मिला</Text>
        )}
      </Modal>
    </div>
  );
}
